<template>
  <div class="relative z-50 w-full" ref="dropdownRef">
    <div class="mb-2" v-if="label.trim() !== ''">
      <label class="font-medium text-base leading-5 text-[#000000]">{{
        label
      }}</label>
    </div>

    <button
      type="button"
      @click="toggleDropdown"
      class="flex justify-between w-full text-sm font-normal rounded-lg focus:outline-none"
      :class="{
        'bg-inputGrey-500 text-inputGrey-600': disabled,
        ' bg-inputGrey-500': !disabled,
        'p-2 border border-gray-300 text-gray-700': tableFilter,
        'px-3.5 py-4 text-inputGrey-900': !tableFilter,
      }"
      :disabled="disabled"
    >
      <span> <slot name="icon"></slot></span>
      <span class="flex-1 text-left">
        <a-tooltip
          :autoAdjustOverflow="true"
          :title="
            selectedLabels.length > 0 ? selectedLabels.join(', ') : buttonText
          "
        >
          <!-- If multiple items selected, display as comma-separated values -->
          {{
            selectedLabels.length > 0
              ? trimText(selectedLabels.join(", "), 48)
              : buttonText
          }}
        </a-tooltip>
      </span>

      <section>
        <div v-if="!loading">
          <span
            :class="{
              'rotate-180 ease-in-out duration-700': isOpen,
              'rotate-0 ease-in-out duration-700': !isOpen,
            }"
            ><img v-if="!disabled" src="@/assets/svg/arrow-down.svg" alt="" />
            <img v-else src="@/assets/svg/arrow-down-disabled.svg" alt="" />
          </span>
        </div>

        <ClipLoaderVue v-else color="inputGrey-900" :size="20" />
      </section>
    </button>

    <transition
      @before-enter="beforeEnter"
      @enter="enter"
      @before-leave="beforeLeave"
      @leave="leave"
    >
      <div
        v-if="isOpen"
        class="mt-2 bg-inputGrey-50 border border-inputGrey-600 rounded-lg text-green-500 px-3 py-4 h-[266px] overflow-y-scroll main w-full"
        :class="{ absolute: isOverlay }"
      >
        <ul class="h-[266px]">
          <li v-if="hasSearch">
            <InputSystem
              :label="''"
              :passwordRevealToggle="false"
              :clearSearch="false"
              :placeholder="'Search'"
              :status="'dropdownSearch'"
              :type="'text'"
              :forgotPassword="false"
              :validation="false"
              v-model="searchQuery"
              @clear="clearInput($event)"
              ><img src="@/assets/svg/search.svg" class="w-5 h-5"
            /></InputSystem>
          </li>
          <li class="w-36 mb-2">
            <ButtonVue
              radius="small"
              size="extraSmall"
              theme="greyOutline"
              @handleClick="toggleSelectAll"
            >
              <img src="../../../assets/svg/plus-circle-black.svg" />
              <span class="text-sm text-gray-700 font-medium"> Select all</span>
            </ButtonVue>
          </li>
          <li
            v-for="(option, index) in filteredItems"
            :key="index"
            @click="toggleOption(option)"
            class="px-4 py-3 mb-2 text-sm font-normal rounded-lg cursor-pointer text-inputGrey-900 border-gray-200 hover:bg-inputGrey-600 focus:outline-none flex gap-x-1 items-center"
            :class="
              selectedLabels.includes(option.label)
                ? 'bg-inputGrey-600'
                : 'bg-inputGrey-500'
            "
          >
            <!-- Use checkbox instead of checkmark SVG -->
            <span class="mr-2">
              <input
                type="checkbox"
                :checked="isSelected(option)"
                class="custom-checkbox"
              />
            </span>
            <span>
              {{ option.label }}
            </span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import EpumpInputVue from "@/components/UI/Inputs/EpumpInput.vue";
import { trimText } from "@/core/helpers/utility";

export default {
  props: {
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    buttonText: {
      type: String,
      default: "Select",
    },
    isOverlay: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    tableFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: "",
    },
    modelValue: {
      type: Array, // Changed to Array for multiple selection
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      trimText,
    };
  },
  computed: {
    filteredItems() {
      const searchLower = this.searchQuery.toLowerCase();
      return this.options.filter((item) => {
        return item.label.toLowerCase().includes(searchLower);
      });
    },
    selectedLabels() {
      // Return the labels of selected options
      return this.options
        ?.filter((option) => this.modelValue?.includes(option.value))
        ?.map((option) => option.label);
    },
  },
  components: {
    EpumpInputVue,
  },

  watch: {
    modelValue(newVal) {
      // Optionally handle changes to the modelValue
    },
  },
  methods: {
    toggleSelectAll() {
      const filteredValues = this.filteredItems.map((item) => item.value);

      if (this.modelValue.length >= filteredValues.length) {
        this.$emit("update:modelValue", []);
      } else {
        const newValues = filteredValues.filter(
          (value) => !this.modelValue.includes(value)
        );
        this.$emit("update:modelValue", [...this.modelValue, ...newValues]);
      }
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    toggleOption(option) {
      const valueIndex = this.modelValue.indexOf(option.value);

      // If value is already selected, remove it, otherwise add it
      if (valueIndex > -1) {
        this.$emit(
          "update:modelValue",
          this.modelValue.filter((_, i) => i !== valueIndex)
        );
      } else {
        this.$emit("update:modelValue", [...this.modelValue, option.value]);
      }
    },
    isSelected(option) {
      return this.modelValue.includes(option.value);
    },
    handleClickOutside(event) {
      if (
        this.$refs.dropdownRef &&
        !this.$refs.dropdownRef.contains(event.target)
      ) {
        this.isOpen = false;
      }
    },
    beforeEnter(el) {
      el.style.opacity = 0;
    },
    enter(el, done) {
      el.offsetHeight;
      el.style.transition = "opacity 0.7s ease-in-out";
      el.style.opacity = 1;
      done();
    },
    beforeLeave(el) {
      el.style.opacity = 1;
    },
    leave(el, done) {
      el.style.transition = "opacity 0.3s ease-in-out";
      el.style.opacity = 0;
      setTimeout(() => {
        done();
      }, 300);
    },
    searchItems(e) {
      this.searchQuery = e.target?.value;
    },
    clearInput(e) {
      this.searchQuery = e;
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.main::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari, and Opera */
}

.main {
  -ms-overflow-style: none; /* Hide scrollbar for Internet Explorer and Edge */
  scrollbar-width: none; /* Hide scrollbar for Firefox */
}
.custom-checkbox {
  top: 3px;
  position: relative;
  width: 16.67px;
  height: 16.67px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: #ffffff;
  border: 1.5px solid #676767;
  cursor: pointer;
  outline: none;
  border-radius: 4px;
}

/* Checkbox when checked */
.custom-checkbox:checked {
  background-color: #ffffff;
  border-color: #676767;
}

.custom-checkbox:checked::before {
  content: "";
  position: absolute;
  top: 2px;
  left: 5.5px;
  width: 4px;
  height: 9px;
  border: solid #676767;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
</style>
